import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { formatDateRelative, pluralize } from 'Utils/text';
import 'Components/atoms/vm-profile-image/vm-profile-image';
import '../vm-video-info-bottom-sheet/vm-video-info-bottom-sheet';
import '../vm-video-info-cards/vm-video-info-cards';

import Styles from './vm-video-info.scss';

export interface Contributor {
    name: string;
    avatar?: string;
    profileUrl?: string;
}

export interface Byline {
    name: string;
    href?: string;
    avatar?: string;
}

@customElement('vm-video-info')
export class VmVideoInfo extends LitElement {
    static override styles = Styles;

    @property({ type: Object }) byline: Byline | null = null;
    @property({ type: Array }) contributors: Contributor[] = [];
    @property({ type: String, attribute: 'published-ago' }) publishedAgo = new Date().toISOString();
    @property({ type: Number, attribute: 'view-count' }) viewCount = 0;
    @property({ type: Number, attribute: 'thumbs-up-count' }) thumbsUpCount = 0;
    @property({ type: String, attribute: 'video-title' }) videoTitle = '';
    @property({ type: String, attribute: 'video-slug' }) videoSlug = '';
    @property({ type: String }) description = '';
    @property({ type: String, attribute: 'content-action-info' }) contentActionInfo = '';
    @property({ type: String, attribute: 'content-action-url' }) contentActionUrl = '';
    @property({ type: String, attribute: 'safety-information' }) safetyInformation = '';
    @property({ type: Boolean, attribute: 'display-isi-preview' }) displayIsiPreview = false;

    @state() private _sheetOpen = false;
    @state() private _sheetInitialHeight = 0;
    @state() private _subtitleTruncated = false;

    private _subtitleResizeObserver: ResizeObserver | null = null;

    private checkSubtitleTruncation() {
        const container = this.shadowRoot?.querySelector<HTMLElement>('.vm-video-info__mobile-subtitle');
        if (!container) {
            this._subtitleTruncated = false;
            return;
        }
        this._subtitleTruncated = container.scrollHeight > container.clientHeight;
    }

    private observeSubtitle() {
        this._subtitleResizeObserver?.disconnect();
        const container = this.shadowRoot?.querySelector('.vm-video-info__mobile-subtitle');
        if (container) this._subtitleResizeObserver?.observe(container);
    }

    override firstUpdated() {
        this._subtitleResizeObserver = new ResizeObserver(() => this.checkSubtitleTruncation());
        this.observeSubtitle();
        this.checkSubtitleTruncation();
    }

    override updated(changedProperties: PropertyValues) {
        if (changedProperties.has('byline') || changedProperties.has('contributors')) {
            this.observeSubtitle();
            this.checkSubtitleTruncation();
        }
        // After un-truncating, verify the inline layout doesn't overflow
        if (changedProperties.has('_subtitleTruncated') && !this._subtitleTruncated) {
            queueMicrotask(() => {
                const container = this.shadowRoot?.querySelector<HTMLElement>('.vm-video-info__mobile-subtitle');
                if (container && container.scrollHeight > container.clientHeight) {
                    this._subtitleTruncated = true;
                }
            });
        }
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._subtitleResizeObserver?.disconnect();
        this._subtitleResizeObserver = null;
    }

    private renderContributorName(contributor: Contributor) {
        const contributorHtml = contributor.profileUrl
            ? html`<a class="vm-video-info__contributor-link" href="${contributor.profileUrl}">${contributor.name}</a>`
            : html`${contributor.name}`;
        return html`<span class="vm-video-info__contributor-name">${contributorHtml}</span>`;
    }

    // place `,` between each contributor except the last which gets an `and`
    private joinContributors() {
        return this.contributors
            .map((c) => this.renderContributorName(c))
            .flatMap((item, i) => {
                if (i === 0) return [item];
                return [' • ', item];
            });
    }

    private renderFeaturing() {
        if (this.contributors.length === 0) return nothing;
        return html`${this.joinContributors()}`;
    }

    private handleMoreInfoClick() {
        const actionsWrap = this.closest<HTMLElement>('.video-js__actions-wrap--2026-01-1');
        const videoWrap = actionsWrap
            ?.closest<HTMLElement>('.video-js__outer-wrap--2026-01-1')
            ?.querySelector<HTMLElement>('.video-js__video-wrap--2026-01-1');

        if (videoWrap && actionsWrap) {
            const videoWrapBoundingClientRect = videoWrap.getBoundingClientRect();
            // After scrolling the video wrap to the top of the viewport, the actions wrap
            // will sit this many pixels below it — the gap between them is layout-fixed.
            const actionsOffsetFromVideo = actionsWrap.getBoundingClientRect().top - videoWrapBoundingClientRect.top;
            this._sheetInitialHeight = window.innerHeight - actionsOffsetFromVideo;
            // Scroll synchronously so the new scroll position is committed before the
            // bottom sheet's open handler locks the body with `position: fixed`.
            window.scrollTo({ top: videoWrapBoundingClientRect.top + window.scrollY, behavior: 'auto' });
        } else {
            this._sheetInitialHeight = actionsWrap ? window.innerHeight - actionsWrap.getBoundingClientRect().top : 0;
        }

        this._sheetOpen = true;
        this.dispatchEvent(new CustomEvent('more-info-click', { bubbles: true, composed: true }));
    }

    private handleSheetClose() {
        this._sheetOpen = false;
    }

    private renderMobileSubtitle() {
        if (!this.byline?.name && this.contributors.length === 0) return nothing;

        const bylinePart = this.byline?.name ? html`${this.byline.name}` : nothing;
        const featuringPart =
            this.contributors.length > 0
                ? html`${this.byline?.name ? ' • ' : ''}${this.contributors.map((c) => c.name).join(' • ')}`
                : nothing;

        const truncatedClass = this._subtitleTruncated ? ' vm-video-info__mobile-subtitle--truncated' : '';
        return html`<button
            type="button"
            class="vm-video-info__mobile-subtitle${truncatedClass}"
            @click=${this.handleMoreInfoClick}
        >
            <span class="vm-video-info__mobile-subtitle-text">${bylinePart}${featuringPart}</span
            ><span class="vm-video-info__more-info">...more info</span>
        </button>`;
    }

    private renderBylineName() {
        if (!this.byline) return nothing;
        return html`${this.byline.href
            ? html`<a class="vm-video-info__byline-link" href="${this.byline.href}">${this.byline.name}</a>`
            : html`${this.byline.name}`}`;
    }

    private renderAvatars() {
        // Only contributor avatar, it has 32x32px when alone
        if (!this.byline?.name || !this.byline.avatar) {
            const first = this.contributors[0];
            if (!first) return nothing;
            return html`<vm-profile-image
                class="vm-video-info__avatar"
                .imageSrc="${first.avatar ?? ''}"
                .userName="${first.name}"
                fit="cover"
            ></vm-profile-image>`;
        }

        // this is the absolutely positioned avatar when channel avatar is available
        const renderContributorAvatar = () => {
            const first = this.contributors[0];
            if (!first) return nothing;

            return html`<vm-profile-image
                class="vm-video-info__contributor-avatar"
                .imageSrc="${first.avatar ?? ''}"
                .userName="${first.name}"
                fit="cover"
            ></vm-profile-image>`;
        };

        return html`
            <div class="vm-video-info__avatars">
                ${this.byline?.avatar
                    ? html`<vm-profile-image
                          class="vm-video-info__byline-logo"
                          .imageSrc="${this.byline.avatar}"
                          .userName="${this.byline.name}"
                          fit="contain"
                      ></vm-profile-image>`
                    : nothing}
                ${renderContributorAvatar()}
            </div>
        `;
    }

    override render() {
        return html`
            <div class="vm-video-info__wrap">
                <h1 class="vm-video-info__title">${this.videoTitle}</h1>
                <div class="vm-video-info__subtitle-wrap">
                    ${this.renderAvatars()}
                    <div class="vm-video-info__text">
                        ${this.byline?.name
                            ? html`<h4 class="vm-video-info__subtitle">${this.renderBylineName()}</h4>`
                            : nothing}
                        ${this.renderMobileSubtitle()}
                        <p class="vm-video-info__meta">
                            ${this.contributors.length > 0
                                ? html`<span class="vm-video-info__meta-item vm-video-info__meta-item--hide-mobile"
                                          >${this.renderFeaturing()}</span
                                      >
                                      <span class="vm-video-info__meta-separator vm-video-info__meta-item--hide-mobile">
                                          &bull;
                                      </span>`
                                : nothing}
                            <span class="vm-video-info__meta-item">${formatDateRelative(this.publishedAgo)}</span>
                            <span class="vm-video-info__meta-separator"> &bull; </span>
                            <span class="vm-video-info__meta-item"
                                >${this.viewCount.toLocaleString()} view${pluralize(this.viewCount)}</span
                            >
                        </p>
                    </div>
                    <slot name="actions" class="vm-video-info__actions-slot"></slot>
                </div>
            </div>
            <vm-video-info-bottom-sheet
                .open=${this._sheetOpen}
                .initialHeight=${this._sheetInitialHeight}
                .videoTitle=${this.videoTitle}
                .videoSlug=${this.videoSlug}
                .viewCount=${this.viewCount}
                .thumbsUpCount=${this.thumbsUpCount}
                .publishedAgo=${this.publishedAgo}
                .contributors=${this.contributors}
                .byline=${this.byline}
                .description=${this.description}
                .contentActionInfo=${this.contentActionInfo}
                .contentActionUrl=${this.contentActionUrl}
                .safetyInformation=${this.displayIsiPreview ? '' : this.safetyInformation}
                @vm-close=${this.handleSheetClose}
            ></vm-video-info-bottom-sheet>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-info': VmVideoInfo;
    }
}
